<template>
  <div v-if="loading" class="sidebar-content-container">
    <TheLoader :active="true"/>
  </div>
  <div v-else-if="!loading && items.length < 1" class="sidebar-content-container">
   <TheEmptyList :title="'Ingen historikk'" :large="false" :icon="'button-glyph-history'"/>
  </div>
  <overlay-scrollbars v-else :options="{ className: 'sidebar-content-container os-theme-dark', scrollbars: { visibility: 'auto', autoHide: 'leave', autoHideDelay: 400, dragScrolling: true, clickScrolling: false, touchSupport: true, snapHandle: false } }">
    <table class="small clickable" v-if="!loading && items.length > 0">
      <tr v-for="item in filteredItems" v-bind:key="item.Brukerloggid">
        <td>
          <a href="javascript:;" class="data-modal" @click="openHistoryModal(item)">
            <span class="avatar name right" :class="strToAvatarClass(item.bruker.initials)" v-if="item.bruker">
              {{item.bruker.initials}}
              <span class="name">{{item.bruker.full_name}}</span>
            </span>
            <span class="avatar name right" :class="strToAvatarClass('-')" v-else>
               -
              <span class="name">Ikke tilgjengelig</span>
            </span>

            <span class="title-container">
              <template v-if="item.Tabell === 'BilagPDF'">
                <span class="title" v-if="getBilagSentEmail(item)">Bilag sendt til {{getBilagSentEmail(item)}}</span>
                <span class="title" v-else>Bilag sendt</span>
              </template>
              <template v-else-if="item.Tabell === 'kundeadresse'">
                <span class="title" v-if="item.Endring === 'ukInsert'">Kundeadresse opprettet</span>
                <span class="title" v-else-if="item.Endring === 'ukModify'">Kundeadresse endret</span>
                <span class="title" v-else-if="item.Endring === 'ukDelete'">Kundeadresse slettet</span>
              </template>
              <template v-else-if="item.Tabell === 'kundeEmail'">
                <span class="title">E-post sendt til kunde</span>
              </template>
              <template v-else>
                <span class="title" v-if="item.Endring === 'ukInsert'">Kunde opprettet</span>
                <span class="title" v-else-if="item.Endring === 'ukModify'">Kunde endret</span>
                <span class="title" v-else-if="item.Endring === 'ukDelete'">Kunde slettet</span>
              </template>
              <span class="timestamp">{{item.Dato | avantidate}}</span>
            </span>
          </a>
        </td>
      </tr>
      <tr>
        <td style="text-align: center" colspan="2" v-if="!everythingLoaded && pageCount > 1">
          <div style="margin-top: 10px">
            <button href="javascript:;" @click="loadMore()" class="button secondary action glyph" v-if="!loading && !updating">
              <svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-add"></use></svg>
              <span>Last fler</span>
            </button>
            <button href="javascript:;" class="button secondary action glyph loading" v-else>
              <svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-add"></use></svg>
              <span>Laster fler <span class="loader-spinner"></span></span>
            </button>
          </div>
        </td>
        <td style="text-align: center" colspan="2" v-if="everythingLoaded || pageCount <= 1">
          <div style="margin-top: 10px">
            Alt har blitt lastet inn
          </div>
        </td>
      </tr>
    </table>

    <div class="expandable-search" :class="{'active': searchBoxActive, 'search-active': search != ''}" v-if="!loading && items.length > 0">
      <div class="input-container search no-margin comment">
        <input  class="rounded" type="text" placeholder="Søk i historikk…" v-model="search" v-on:keydown="onSearchInput" v-on:blur="toggleSearchbox(false)" v-on:focus="toggleSearchbox(true)">
        <button class="glyph-button" type="submit">Søk <svg width="14px" height="14px" viewBox="0 0 14 14"><use xlink:href="/static/sprites.svg#glyph-input-search"></use></svg></button>
        <a href="javascript:;" @click="clearSearch()" class="cancel-search"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-close"></use></svg></a>
      </div>
    </div>
  </overlay-scrollbars>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'KundeCardSidebarTabHistorikk',
  props: {
    kunde: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      currentPage: 1, // Just for the pagination
      search: '',
      searchBoxActive: false,
      skipLogKeys: [
        'kundeadresse',
        'Sist_endret_av',
        'Sist_endret_tid',
        'Hbversion'
      ]
    }
  },
  created () {
    // Set current page
    this.currentPage = this.queryParams.page || 1

    // Debounced input
    this.onSearchInput = this._.debounce(this.onSearchInput, 500)
  },
  computed: {
    ...mapGetters('kundehistory', [
      'items',
      'pageCount',
      'totalCount',
      'everythingLoaded',
      'queryParams',
      'loading',
      'updating'
    ]),
    filteredItems: function () {
      if (!this.search) {
        return this.items
      }

      return this.items.filter((i) => {
        return this._.includes(this._.toLower(i.Logg), this.search)
      })
    }
  },
  methods: {
    openHistoryModal: function (item) {
      if (item.Tabell === 'BilagPDF') {
        const bilag = this.getBilagSentBilag(item)

        this.openModal(
          'history-item-bilag-pdf-modal',
          {
            bilag: bilag,
            item: item,
            internalWindow: true
          },
          'split'
        )
      } else {
        this.openModal(
          'history-item-modal',
          {
            item: item
          }
        )
      }
    },

    getBilagSentBilag: function (item) {
      if (item.Tabell === 'BilagPDF') {
        const data = item.Logg ? JSON.parse(item.Logg) : null

        if (data && data.Data && data.Data.Bilag) {
          return data.Data.Bilag
        }
      }

      return null
    },

    getBilagSentEmail: function (item) {
      if (item.Tabell === 'BilagPDF') {
        const data = item.Logg ? JSON.parse(item.Logg) : null

        if (data && data.Data && data.Data.Email) {
          return data.Data.Email
        }
      }

      return null
    },
    clearSearch: function () {
      this.search = ''
      this.toggleSearchbox(false)
    },
    toggleSearchbox: function (mode) {
      if (mode === false) {
        this.searchBoxActive = this.search !== ''
      } else {
        this.searchBoxActive = true
      }
    },
    onSearchInput: function (e) {
      // In it's own method, so we can have debounce
      this.search = this._.toLower(e.target.value.trim())
    },
    loadMore: function () {
      let page = this.currentPage + 1

      if (page > this.pageCount) {
        console.log('Last page already loaded')
        return
      }

      // Make query to load more...
      let copiedParams = this._.clone(this.queryParams)
      this.currentPage = page
      copiedParams.page = this.currentPage

      this.$store.dispatch('kundehistory/reload', {
        Kundenr: this.kunde.Kundenr,
        queryParams: copiedParams
      }).then((response) => {
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Admin/KundeCardSidebarTabHistorikk.vue